import { inject } from '@angular/core';
import { Router, CanActivateFn, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { TenantStore } from '../../stores/tenant.store';
import { AuthStore } from '../../stores/auth.store';
import { AuthService } from '../services/auth.service';
import { NotificationService } from '../services/notification.service';
import { LoggerService } from '../services/logger.service';
import { ROLES } from '../constants/permissions';
import { sanitizeGuardReturnUrl } from './role.guard';
import { AppStorageService } from '../services/app-storage.service';

// GAP-FE-C013: the 'auth_return_url' / 'tenant_selection_return_url' writes below
// are session-scoped (consumed once, never persisted). They go through
// AppStorageService's session methods (sessionStorage-backed) so they are
// SSR-safe and mockable — no longer raw sessionStorage. Session scope is
// preserved (setSessionItem writes to sessionStorage, not localStorage).

/** Default timeout for tenant guard async operations (10 seconds) */
const TENANT_GUARD_TIMEOUT_MS = 10000;

/**
 * Wraps a promise with a timeout. Rejects with TimeoutError if the promise
 * doesn't resolve within the specified duration.
 */
function withTimeout<T>(promise: Promise<T>, timeoutMs: number, operation: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(
        () => reject(new Error(`Timeout: ${operation} exceeded ${timeoutMs}ms`)),
        timeoutMs
      )
    ),
  ]);
}

/**
 * Tenant Guard
 * 
 * Ensures that a tenant is selected before accessing protected routes.
 * Redirects to tenant selection page if no tenant is selected.
 * 
 * Features:
 * - Checks for current tenant selection
 * - Auto-loads available tenants if authenticated
 * - Auto-selects single tenant if only one available
 * - Redirects to tenant selection for multi-tenant users
 * - Allows system admins to bypass tenant requirement
 */
export const tenantGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const tenantStore = inject(TenantStore);
  const authStore = inject(AuthStore);
  const authService = inject(AuthService);
  const router = inject(Router);
  const notificationService = inject(NotificationService);
  const logger = inject(LoggerService);
  const storage = inject(AppStorageService);

  // Check if user is authenticated first
  // Use AuthService.isAuthenticated() as the single source of truth
  // Do NOT check hasToken separately - the access token may be null during refresh cycles
  // but the user is still authenticated via refresh token (HttpOnly cookie)
  const isAuthenticated = authService.isAuthenticated();

  if (!isAuthenticated) {
    logger.debug('[TenantGuard] Not authenticated', { isAuthenticated });
    // Store return URL in sessionStorage (read by login component)
    storage.setSessionItem('auth_return_url', sanitizeGuardReturnUrl(state.url));
    router.navigate(['/auth/login']);
    return false;
  }

  // COLD-LOAD RACE FIX: the system-admin tenant bypass below reads
  // user.roles, which is hydrated asynchronously by loadEffectivePermissions().
  // Wait for that hydration to settle (finite timeout, fail-safe) so a
  // system-admin's bypass isn't missed because the guard ran with empty roles.
  if (!authService.authReady()) {
    await authService.whenAuthReady();
  }

  const user = authStore.user();
  
  // System admins can bypass tenant requirement if specified in route data
  if (user?.roles?.includes(ROLES.SYSTEM_ADMIN) && route.data['allowSystemAdminBypass']) {
    return true;
  }

  // Check if tenant is already selected
  if (tenantStore.currentTenant()) {
    return true;
  }

  // Try to load available tenants with timeout protection
  if (!tenantStore.availableTenants().length) {
    try {
      await withTimeout(
        tenantStore.loadAvailableTenants(),
        TENANT_GUARD_TIMEOUT_MS,
        'loadAvailableTenants'
      );
    } catch (error) {
      logger.error('[TenantGuard] Failed to load tenants', { error });
      notificationService.showError(
        'Connection Error',
        'Unable to load tenant information. Please try again.'
      );
      router.navigate(['/auth/login']);
      return false;
    }
  }

  const availableTenants = tenantStore.availableTenants();

  // If still no tenant after loading
  if (!tenantStore.currentTenant()) {
    if (availableTenants.length === 0) {
      // No tenants available for this user
      notificationService.showError('Access Denied', 'No tenants available for your account');
      router.navigate(['/auth/login']);
      return false;
    } else if (availableTenants.length === 1) {
      // Auto-select single tenant with timeout protection
      const tenant = availableTenants[0];
      logger.debug('[TenantGuard] Auto-selecting single tenant', { tenant });
      let switched: boolean;
      try {
        switched = await withTimeout(
          tenantStore.switchTenant(tenant.id),
          TENANT_GUARD_TIMEOUT_MS,
          'switchTenant'
        );
      } catch (error) {
        logger.error('[TenantGuard] Failed to switch tenant', { error });
        notificationService.showError(
          'Connection Error',
          'Unable to set tenant context. Please try again.'
        );
        router.navigate(['/auth/login']);
        return false;
      }

      // SENTRY-RANK4: TenantStore.switchTenant() resolves `false` on failure —
      // it never rejects — so the catch above only covers timeouts/unexpected
      // rejections. Previously the boolean was DISCARDED and a follow-up
      // currentTenant() probe emitted a second, context-free error ('Failed to
      // set tenant context') plus a blind redirect to /auth/login even though
      // the user is still authenticated. Inspect the result instead: on
      // failure, surface ONE attributable error explaining that the tenant
      // switch failed (including the store's recorded reason when available)
      // and send the user to tenant selection — where the switch can be
      // retried with context — rather than back to login.
      if (switched === false || !tenantStore.currentTenant()) {
        const reason = tenantStore.error();
        logger.error('[TenantGuard] Tenant switch failed during auto-select', {
          tenantId: tenant.id,
          tenantName: tenant.name,
          reason,
        });
        notificationService.showError(
          'Tenant Switch Failed',
          `Could not switch to tenant "${tenant.name}"${reason ? ` (${reason})` : ''}. ` +
            'Your access may have changed — please retry or contact your administrator.'
        );
        storage.setSessionItem('tenant_selection_return_url', sanitizeGuardReturnUrl(state.url));
        router.navigate(['/tenant-selection']);
        return false;
      }

      return true;
    } else {
      // Multiple tenants available - redirect to selection
      notificationService.showInfo('Tenant Required', 'Please select a tenant to continue');
      // Store return URL in sessionStorage for tenant selection page
      storage.setSessionItem('tenant_selection_return_url', sanitizeGuardReturnUrl(state.url));
      router.navigate(['/tenant-selection']);
      return false;
    }
  }

  return true;
};

/**
 * Tenant Feature Guard
 * 
 * Checks if the current tenant has access to a specific feature.
 * Used to protect feature-specific routes based on tenant subscription.
 */
export const tenantFeatureGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot
) => {
  const tenantStore = inject(TenantStore);
  const router = inject(Router);
  const notificationService = inject(NotificationService);
  const logger = inject(LoggerService);

  // Get required feature from route data
  const requiredFeature = route.data['requiredFeature'] as string;

  if (!requiredFeature) {
    logger.warn('TenantFeatureGuard used without requiredFeature in route data');
    return true;
  }

  // Check if tenant has the required feature
  if (tenantStore.hasAccessToFeature(requiredFeature)) {
    return true;
  }

  // Feature not available for this tenant
  notificationService.showWarning(
    'Feature Unavailable', 
    `The ${requiredFeature} feature is not available for your tenant`
  );
  
  router.navigate(['/dashboard']);
  return false;
};
